/**
 * What the boards are *called*, in one place.
 *
 * Three surfaces name a (metric, period) now — the `/leaderboard` page, the champion callout and
 * the death panel's standing — and the one thing that must not drift between them is which
 * question a board is answering. `pellets` and `dots` are the same column read two different
 * ways (`max` against `sum`), so a screen that printed "Dots" over either of them without saying
 * which would be quietly wrong on one of the two. Presentation only: nothing here decides
 * anything, and every number these labels sit beside comes from the server.
 */

import type { LeaderboardMetric, LeaderboardPeriod } from '@chompdome/shared/constants';

export const PERIOD_LABELS: Record<LeaderboardPeriod, string> = {
  hour: 'This hour',
  day: 'Today',
  week: 'This week',
  month: 'This month',
  year: 'This year',
  all: 'All time',
};

/**
 * The same window as a fragment that can follow a name — "top dog **this hour**" — rather than
 * as a heading. `PERIOD_LABELS` reads wrong in a sentence ("Today" mid-clause), and a callout is
 * a sentence.
 */
export const PERIOD_PHRASES: Record<LeaderboardPeriod, string> = {
  hour: 'this hour',
  day: 'today',
  week: 'this week',
  month: 'this month',
  year: 'this year',
  all: 'of all time',
};

/**
 * What each board is called, and — the part that has to be said — whether its number is the best
 * of one run or everything added up over the period. Two of the seven are a personal best inside
 * the window and five are a total, and a column heading is where a reader finds that out.
 *
 * `short` is the death panel's and the callout's version of `tab`: the same board named in the
 * space a phone has for it.
 */
export const METRIC_LABELS: Record<
  LeaderboardMetric,
  { tab: string; short: string; column: string; blurb: string }
> = {
  mass: {
    tab: 'Biggest run',
    short: 'Mass',
    column: 'Mass',
    blurb: 'The largest a single blob got. One run, at its peak — not what it finished on.',
  },
  pellets: {
    tab: 'Best dot run',
    short: 'Best dots',
    column: 'Dots',
    blurb: 'The most dots swallowed in a single run.',
  },
  dots: {
    tab: 'Most dots',
    short: 'Dots',
    column: 'Dots',
    blurb: 'Every dot swallowed in the period, added up across every run.',
  },
  kills: {
    tab: 'Chomps',
    short: 'Chomps',
    column: 'Chomps',
    blurb: 'Players eaten, added up across every run in the period.',
  },
  deaths: {
    tab: 'Most chomped',
    short: 'Chomped',
    column: 'Deaths',
    blurb: 'Times eaten in the period. Quitting and the room closing do not count.',
  },
  massGained: {
    tab: 'Mass gained',
    short: 'Gained',
    column: 'Gained',
    blurb: 'All the mass taken in — dots, blobs and duel winnings — across the period.',
  },
  matches: {
    tab: 'Runs played',
    short: 'Runs',
    column: 'Runs',
    blurb: 'Runs finished in the period. The one board that rewards showing up.',
  },
};

/**
 * One title, as a sentence everybody in the deployment reads.
 *
 * **The deaths board is a joke and is written as one.** It is the title the player report asked
 * for by name, and the only way it works is if it reads as a medal for taking part rather than
 * as a dig — so it congratulates the holder for keeping getting back up, and it never says
 * anybody is bad at the game. It is also safe to name the holder: guests are excluded from these
 * aggregations on the server (a guest row is reclaimed by a device id anybody can send), so a
 * champion is always somebody who chose to put their name on a board.
 */
export function championHeadline(
  metric: LeaderboardMetric,
  period: LeaderboardPeriod,
  name: string,
): string {
  const when = PERIOD_PHRASES[period];
  switch (metric) {
    case 'mass':
      return `${name} is top dog ${when}`;
    case 'pellets':
      return `${name} ate the biggest bowl of dots ${when}`;
    case 'dots':
      return `${name} has hoovered up the most dots ${when}`;
    case 'kills':
      return `${name} has chomped the most players ${when}`;
    case 'deaths':
      return `${name} has been chomped the most ${when} — and keeps coming back`;
    case 'massGained':
      return `${name} has packed on the most mass ${when}`;
    case 'matches':
      return `${name} has played the most runs ${when}`;
  }
}
